import { HttpClient } from '@angular/common/http';
import { inject, Injectable, Injector } from '@angular/core';
import { BehaviorSubject, firstValueFrom, Observable } from 'rxjs';

import { AppTranslateService } from './app-translate.service';
import { DictionaryAPIService } from './dictionary-api.service';

import { filter, tap } from 'rxjs/operators';
import { TvLoginStore } from '../../pages/app/services/app.store';
import Language from '../entities/language';
import { LoginAPIService } from './login-api.service';

@Injectable({
  providedIn: 'root',
})
export class LanguageService {
  readonly tvLoginStore = inject(TvLoginStore);

  static SUPER_SYSTEM_LANGUAGE_ID = 0;

  selectedSystemLanguage: BehaviorSubject<Language> = new BehaviorSubject<Language>(new Language());
  systemLanguages: BehaviorSubject<Language[]> = new BehaviorSubject<Language[]>([]);
  clientLanguages: BehaviorSubject<Language[]> = new BehaviorSubject<Language[]>([]);

  private http: HttpClient = null;

  constructor(
    private dictionaryAPIService: DictionaryAPIService,
    private appTranslateService: AppTranslateService,
    private injector: Injector,
    private loginAPIService: LoginAPIService
  ) {
    this.http = this.injector.get(HttpClient);
  }

  getSelectedLanguage(): Language {
    return this.selectedSystemLanguage.value;
  }

  findLanguage(languageId: number): Language {
    const allLanguages = this.systemLanguages.value;
    return allLanguages.find((language) => language.id === languageId);
  }

  loadSystemLanguages(): Observable<Language[]> {
    return this.http.get<Language[]>(this.dictionaryAPIService.systemLanguages()).pipe(
      tap((languages) => {
        const preLanguages = this.prepareLanguages(languages);
        const languagesWithoutSuperSystem = preLanguages.filter(
          (language) => language.id !== LanguageService.SUPER_SYSTEM_LANGUAGE_ID
        );

        this.setSystemlanguages(languagesWithoutSuperSystem);
      })
    );
  }

  isContainsClientLanguages(clientLanguagesIds: number[]): boolean {
    if (clientLanguagesIds == null || clientLanguagesIds.length === 0) {
      return false;
    }

    return clientLanguagesIds.some((id) => this.selectedSystemLanguage.value.id == id);
  }

  changeSystemLanguage(language: Language) {
    if (language) {
      this.setSelectedSystemLanguage(language);
      this.saveSelectedLanguage();
      return this.appTranslateService.setLanguage(language.id);
    }

    console.warn('en attempting to change language to', language);
    return Promise.resolve();
  }

  changeSystemLanguageAndPutToStore(language: Language) {
    if (language) {
      this.tvLoginStore.setSelectedSystemLanguageAndPutItToStore(language);

      return this.changeSystemLanguage(language);
    }

    console.warn('en attempting to save language.id from', language);
    return Promise.resolve();
  }

  filterSystemlanguage(clientLanguagesIds: number[]): Language[] {
    return this.systemLanguages.value.filter((language) => clientLanguagesIds.find((id) => id == language.id));
  }

  setDefaultLanguage() {
    this.setSelectedSystemLanguage(this.getDefaultLanguage());
  }

  setClientLanguages(languages: Language[]) {
    this.clientLanguages.next(languages);
  }

  setSystemlanguages(languages: Language[]) {
    this.systemLanguages.next(languages);
  }

  setSelectedSystemLanguageById(languageId: number): void {
    const language = this.findLanguage(languageId);

    if (language) {
      this.setSelectedSystemLanguage(language);
      this.tvLoginStore.setSelectedSystemLanguageAndPutItToStore(language);
    } else {
      console.log('cannot find language', languageId);
    }
  }

  /**
   * @deprecated This method is deprecated and will be removed in future versions.
   * Use `tvLoginStore.setSelectedSystemLanguage` instead.
   */
  private setSelectedSystemLanguage(language: Language) {
    this.selectedSystemLanguage.next(language);
    this.tvLoginStore.setSelectedSystemLanguage(language);
  }

  saveSelectedLanguage(): Promise<any> {
    const languageId = parseInt(this.tvLoginStore.getSelectedLanguageId());

    if (isNaN(languageId)) {
      return Promise.resolve({});
    }

    return this.loginAPIService
      .setCSRFToken()
      .then(() => this.http.put(this.dictionaryAPIService.updatelanguage(), { languageId }).toPromise());
  }

  sortLanguages(language: Language[], firedName: string) {
    return language.sort((a, b) => {
      if (a[firedName].toLowerCase() > b[firedName].toLowerCase()) {
        return 1;
      }

      if (a[firedName].toLowerCase() < b[firedName].toLowerCase()) {
        return -1;
      }

      return 0;
    });
  }

  async getSystemLanguages$(): Promise<Language[]> {
    return firstValueFrom(
      this.systemLanguages.asObservable().pipe(filter((languages: Language[]) => languages?.length > 0))
    );
  }

  isLanguageSelected(): boolean {
    return this.getSelectedLanguage().id != null;
  }

  private prepareLanguages(languages): Language[] {
    return languages.map((language) => new Language(language));
  }

  private getDefaultLanguage() {
    return this.findLanguage(AppTranslateService.defaultSystemLanguageId);
  }
}
